/**
 * Production payroll (Czapelska) admin API: `/api/web-admin/production-payroll/*`.
 *
 * Mirrors the HTTP contract in docs/plans/2026-09-27-production-payroll.md.
 * Money and kilograms travel as decimal strings; the UI never does money math
 * beyond display sums of already-rounded server lines.
 */

import { api } from './client';

const BASE = '/production-payroll';

export type PayrollRole = 'regular' | 'trainee';
export type PayrollDayStatus = 'draft' | 'needs_review' | 'confirmed';
export type PayrollSourceKind = 'telegram' | 'manual';
export type PayrollAdjustmentKind =
  | 'advance'
  | 'card_payout'
  | 'cash_payout'
  | 'bonus'
  | 'deduction';
export type PayrollReviewReason =
  | 'unresolved_worker'
  | 'trainee_without_hours'
  | 'headcount_mismatch'
  | 'implausible_kg'
  | 'agent_question'
  | (string & {});

export interface PayrollLocation {
  id: string;
  name: string;
}

/** `GET /locations` row: ordered by `last_work_date` desc (nulls last), then name. */
export interface PayrollLocationOption extends PayrollLocation {
  last_work_date: string | null;
}

export interface PayrollWorker {
  id: string;
  full_name: string;
  aliases: string[];
  default_role: PayrollRole;
  is_active: boolean;
}

export interface PayrollAttendance {
  id: string;
  worker_id: string | null;
  raw_name: string;
  role: PayrollRole;
  hours: string | null;
  is_shocker: boolean;
  note: string | null;
  kg: string | null;
  pay: string | null;
}

export interface PayrollSourceRef {
  chat_id?: number | string | null;
  message_ids?: number[] | null;
  text?: string | null;
}

export interface PayrollDay {
  id: string;
  work_date: string;
  total_kg: string;
  status: PayrollDayStatus;
  review_reasons: PayrollReviewReason[];
  questions: string[];
  reported_kg_per_person: string | null;
  reported_regular_count: number | null;
  reported_trainee_count: number | null;
  regular_count: number;
  trainee_count: number;
  divisor: string | null;
  kg_per_person: string | null;
  erp_production_kg: string | null;
  source_kind: PayrollSourceKind;
  source_ref: PayrollSourceRef | null;
  notes: string | null;
  version: number;
  attendance: PayrollAttendance[];
}

export interface PayrollSummaryRow {
  worker_id: string;
  full_name: string;
  days_worked: number;
  trainee_hours: string;
  kg: string;
  kg_pay: string;
  trainee_pay: string;
  shocker_days: number;
  shocker_pay: string;
  bonus: string;
  deduction: string;
  advance: string;
  card_payout: string;
  /** Remainder handed out in cash after the month. */
  cash_payout: string;
  total_due: string;
  /** advance + card_payout + cash_payout */
  paid: string;
  to_pay: string;
}

export interface PayrollAdjustment {
  id: string;
  worker_id: string;
  kind: PayrollAdjustmentKind;
  amount: string;
  paid_on: string | null;
  comment: string | null;
  /** The accountant's bill behind a card payout (rachunek number): the amount is exact. */
  document?: string | null;
  /** When that bill is paid (transfer date on the rachunek). */
  pay_date?: string | null;
  created_at: string;
  /** Set only when a create replays a soft-deleted adjustment; the month list never has them. */
  deleted_at?: string | null;
}

/** Card salary transfer from the bank («ZA WYKONANIE: ZA MM/YYYY») matched to a worker. */
export interface PayrollBankCard {
  id: string;
  worker_id: string;
  booking_date: string | null;
  amount: string;
  counterparty_name: string | null;
}

export interface PayrollTotals {
  kg: string;
  earned: string;
  total_due: string;
  paid: string;
  to_pay: string;
  days: number;
  days_unconfirmed: number;
  days_needs_review: number;
}

export interface PayrollSettings {
  rate_per_kg: string;
  trainee_hourly: string;
  trainee_weight: string;
  shocker_monthly: string;
}

export interface PayrollMonth {
  month: string;
  location: PayrollLocation;
  settings: PayrollSettings;
  workers: PayrollWorker[];
  days: PayrollDay[];
  summary: PayrollSummaryRow[];
  adjustments: PayrollAdjustment[];
  /** Card transfers from the bank; they replace the worker's planned card (no transfer date). */
  bank_cards: PayrollBankCard[];
  /** Card adjustments a bank transfer stands for (the plan, a hand-typed copy): not counted. */
  replaced_card_ids: string[];
  totals: PayrollTotals;
  /** Every date of the month with counted ERP production (report or not). */
  erp_days: PayrollErpDay[];
  /** Days whose seller production request waits for approval (no ERP document yet). */
  erp_awaiting: PayrollErpAwaiting[];
}

export interface PayrollErpAwaiting {
  work_date: string;
  requested_at: string | null;
}

export interface PayrollErpDay {
  work_date: string;
  kg: string;
}

/** What the site produced on one day according to ERP production documents. */
export interface PayrollProductionDay {
  date: string;
  documents: Array<{
    id: string;
    production_date: string;
    notes: string | null;
    items_count: number;
    counted_kg: string;
    hot_kg: string;
  }>;
  groups: Array<{
    category: string | null;
    counted: boolean;
    kg: string;
    items: Array<{
      product_id: string;
      name: string;
      quantity: string;
      unit_code: string;
      kg: string | null;
      counted: boolean;
    }>;
  }>;
  counted_kg: string;
  hot_kg: string;
  uncounted_pcs: Array<{ product_id: string; name: string; quantity: string; unit_code: string }>;
}

export interface PayrollAttendanceInput {
  worker_id: string | null;
  raw_name: string;
  role: PayrollRole;
  hours?: string | null;
  is_shocker: boolean;
  note?: string | null;
}

export interface PayrollDayInput {
  location_id: string;
  work_date: string;
  total_kg: string;
  notes?: string | null;
  attendance: PayrollAttendanceInput[];
  expected_version?: number;
}

export interface PayrollWorkerCreate {
  full_name: string;
  aliases?: string[];
  default_role?: PayrollRole;
}

export interface PayrollWorkerPatch {
  full_name?: string;
  /** Full replace. */
  aliases?: string[];
  /** Append (server dedupes case-insensitively, under a row lock). */
  add_aliases?: string[];
  default_role?: PayrollRole;
  is_active?: boolean;
}

export interface PayrollAdjustmentCreate {
  location_id: string;
  worker_id: string;
  /** `YYYY-MM`, same form as the month query. */
  month: string;
  kind: PayrollAdjustmentKind;
  amount: string;
  paid_on?: string | null;
  comment?: string | null;
  client_request_id: string;
}

/** What the signed-in user may do on the payroll page (MD-0097). */
export interface PayrollAccess {
  can_use: boolean;
}

/** One change of production payroll: who (a person on the page or the chat agent) and what. */
export interface PayrollJournalEvent {
  id: string;
  at: string;
  /** The day the change is about, if it is about a day. */
  work_date: string | null;
  worker_id: string | null;
  worker_name: string | null;
  action: string;
  summary: string;
  /** Details, e.g. people added or removed: «+ Дуда». */
  lines: string[];
  source: 'page' | 'agent';
  actor_name: string | null;
}

export interface PayrollJournal {
  month: string;
  /** More lines exist than the reply carries (the newest are shown). */
  truncated: boolean;
  events: PayrollJournalEvent[];
}

export const productionPayrollKeys = {
  all: ['production-payroll'] as const,
  access: () => [...productionPayrollKeys.all, 'access'] as const,
  locations: () => [...productionPayrollKeys.all, 'locations'] as const,
  months: () => [...productionPayrollKeys.all, 'month'] as const,
  month: (locationId: string, month: string) =>
    [...productionPayrollKeys.months(), locationId, month] as const,
  production: (locationId: string, date: string) =>
    [...productionPayrollKeys.all, 'production', locationId, date] as const,
  journals: () => [...productionPayrollKeys.all, 'journal'] as const,
  journal: (locationId: string, month: string, workerId: string | null) =>
    [...productionPayrollKeys.journals(), locationId, month, workerId ?? 'all'] as const,
};

export const productionPayrollApi = {
  access: () => api.webAdmin.get<PayrollAccess>(`${BASE}/access`),

  locations: () => api.webAdmin.get<PayrollLocationOption[]>(`${BASE}/locations`),

  month: (locationId: string, month: string) =>
    api.webAdmin.get<PayrollMonth>(`${BASE}/month`, {
      params: { location_id: locationId, month },
    }),

  journal: (locationId: string, month: string, workerId: string | null) =>
    api.webAdmin.get<PayrollJournal>(`${BASE}/journal`, {
      params: workerId
        ? { location_id: locationId, month, worker_id: workerId }
        : { location_id: locationId, month },
    }),

  productionDay: (locationId: string, date: string) =>
    api.webAdmin.get<PayrollProductionDay>(`${BASE}/production`, {
      params: { location_id: locationId, date },
    }),

  saveDay: (body: PayrollDayInput) => api.webAdmin.put<PayrollDay>(`${BASE}/days`, body),

  confirmDay: (dayId: string, expectedVersion: number) =>
    api.webAdmin.post<PayrollDay>(`${BASE}/days/${encodeURIComponent(dayId)}/confirm`, {
      expected_version: expectedVersion,
    }),

  deleteDay: (dayId: string, expectedVersion: number) =>
    api.webAdmin.delete<unknown>(`${BASE}/days/${encodeURIComponent(dayId)}`, {
      body: { expected_version: expectedVersion },
    }),

  createWorker: (body: PayrollWorkerCreate) =>
    api.webAdmin.post<PayrollWorker>(`${BASE}/workers`, body),

  updateWorker: (workerId: string, body: PayrollWorkerPatch) =>
    api.webAdmin.patch<PayrollWorker>(`${BASE}/workers/${encodeURIComponent(workerId)}`, body),

  createAdjustment: (body: PayrollAdjustmentCreate) =>
    api.webAdmin.post<PayrollAdjustment>(`${BASE}/adjustments`, body, {
      idempotencyKey: body.client_request_id,
    }),

  deleteAdjustment: (adjustmentId: string) =>
    api.webAdmin.delete<unknown>(`${BASE}/adjustments/${encodeURIComponent(adjustmentId)}`),
};
